/**
 * The pure core: step(state, input, ticks) -> state.
 *
 * Sub-systems run in a fixed order so they see consistent state:
 *   playing:
 *     1. deploy smoke   — input.smoke + fuel check
 *     2. move player    — stepCar
 *     3. move enemies   — BFS-driven AI per tile-center
 *     4. expire smokes  — drop those past TTL
 *     5. stun enemies   — any enemy on a live smoke tile
 *     6. pickup flag    — player on uncollected flag tile
 *     7. collide enemy  — kill player if on non-stunned enemy
 *     8. fuel tick      — deplete; transition to dying at 0
 *   dying: tick respawn timer.
 *   cleared/gameover/title: no-op.
 */

import type { GameState, InputFrame } from './types.ts';
import { stepCar } from './movement.ts';
import { checkFlagPickup } from './collection.ts';
import { applyFuelTick } from './fuel.ts';
import { stepEnemies } from './enemies.ts';
import { deploySmoke, expireSmokes, stunEnemiesOnSmoke } from './smoke.ts';
import { checkPlayerBoulderCollision, checkPlayerEnemyCollision, tickDying } from './dying.ts';
import { beginStageTransition, effectivePlayerSpeed, tickStageTransition } from './stage.ts';
import { createGame } from './init.ts';

export function step(state: GameState, input: InputFrame, ticks = 1): GameState {
  let s = state;
  for (let i = 0; i < ticks; i++) s = stepOnce(s, input);
  return s;
}

function stepOnce(state: GameState, input: InputFrame): GameState {
  if (state.phase === 'gameover' && input.start) {
    // Restart: rebuild the run from the same baseSeed and config, preserving
    // nothing of the dead game. Tick counter resets so the new game starts
    // from a clean slate.
    return createGame({ seed: state.baseSeed, config: state.config });
  }
  const ticked: GameState = { ...state, tick: state.tick + 1, events: [] };
  if (ticked.phase === 'playing') return stepPlaying(ticked, input);
  if (ticked.phase === 'dying') return tickDying(ticked);
  if (ticked.phase === 'cleared') return tickStageTransition(beginStageTransition(ticked));
  if (ticked.phase === 'title' && input.start) return { ...ticked, phase: 'playing' };
  return ticked; // gameover (no input), title (no input): hold
}

function stepPlaying(state: GameState, input: InputFrame): GameState {
  let s = state;
  s = deploySmoke(s, input);
  // Boulder-impact check runs BEFORE movement so the death triggers at the
  // exact tile-center boundary where the next tile is a boulder. Takes input
  // so a player who reverses or turns away on the same tick isn't killed for
  // their previous heading.
  s = checkPlayerBoulderCollision(s, input);
  if (s.phase !== 'playing') return s;
  const player = stepCar(s.player, input, s.maze, effectivePlayerSpeed(s));
  s = { ...s, player };
  if (!s.challenging) s = stepEnemies(s);
  s = expireSmokes(s);
  s = stunEnemiesOnSmoke(s);
  s = checkFlagPickup(s);
  if (!s.challenging) s = checkPlayerEnemyCollision(s);
  s = applyFuelTick(s);
  return s;
}
